import { create } from "zustand";
import * as api from "../lib/api";
import type { ApiCalendarEntry, CreateEventInput, UpdateEventInput } from "../lib/types";

interface EventState {
  events: ApiCalendarEntry[];
  loading: boolean;
  currentRange: { start: string; end: string } | null;
  fetchRange: (calendarIds: string[], start: string, end: string) => Promise<void>;
  create: (calendarId: string, input: CreateEventInput) => Promise<ApiCalendarEntry>;
  update: (calendarId: string, eventId: string, input: UpdateEventInput) => Promise<void>;
  remove: (calendarId: string, eventId: string) => Promise<void>;
  mergeEvent: (event: ApiCalendarEntry) => void;
  removeEvent: (eventId: string) => void;
  refetch: (calendarIds: string[]) => Promise<void>;
}

export const useEventStore = create<EventState>((set, get) => ({
  events: [],
  loading: false,
  currentRange: null,

  fetchRange: async (calendarIds: string[], start: string, end: string) => {
    set({ loading: true, currentRange: { start, end } });
    try {
      const results = await Promise.all(calendarIds.map((id) => api.fetchEvents(id, start, end)));
      const events = results.flat();
      set({ events, loading: false });
    } catch {
      set({ loading: false });
    }
  },

  create: async (calendarId: string, input: CreateEventInput) => {
    const event = await api.createEventApi(calendarId, input);
    set((s) => ({ events: [...s.events, event] }));
    return event;
  },

  update: async (calendarId: string, eventId: string, input: UpdateEventInput) => {
    const prev = get().events;
    set((s) => ({
      events: s.events.map((e) =>
        e.id === eventId ? ({ ...e, ...input } as ApiCalendarEntry) : e,
      ),
    }));
    try {
      const event = await api.updateEventApi(calendarId, eventId, input);
      set((s) => ({
        events: s.events.map((e) => (e.id === eventId ? event : e)),
      }));
    } catch {
      set({ events: prev });
      throw new Error("Failed to update event");
    }
  },

  remove: async (calendarId: string, eventId: string) => {
    const prev = get().events;
    set((s) => ({ events: s.events.filter((e) => e.id !== eventId) }));
    try {
      await api.deleteEventApi(calendarId, eventId);
    } catch {
      set({ events: prev });
      throw new Error("Failed to delete event");
    }
  },

  // SSE helpers — merge or remove individual events
  mergeEvent: (event: ApiCalendarEntry) => {
    set((s) => {
      const idx = s.events.findIndex((e) => e.id === event.id);
      if (idx >= 0) {
        const events = [...s.events];
        events[idx] = event;
        return { events };
      }
      return { events: [...s.events, event] };
    });
  },

  removeEvent: (eventId: string) => {
    set((s) => ({ events: s.events.filter((e) => e.id !== eventId) }));
  },

  refetch: async (calendarIds: string[]) => {
    const range = get().currentRange;
    if (range) {
      const results = await Promise.all(
        calendarIds.map((id) => api.fetchEvents(id, range.start, range.end)),
      );
      set({ events: results.flat() });
    }
  },
}));
